import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Team } from 'modules/teams/domain/interfaces/team';
import { editTeam } from 'modules/teams/store/teams';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

import { AppDispatch } from 'store/store';
import TeamForm, { SubmitTeamForm } from './team-form/team-form';

type EditTeamProps = {
  team: Team;
  tournamentId: number;
};

const mapLogo = (logo: File[]) => {
  return logo.length > 0 ? logo[0] : undefined;
};

function isChanged(team: Team, values: Parameters<SubmitTeamForm>[0]['values']) {
  if (team.name !== values.name) return true;
  if (team.shortName !== values.shortName) return true;
  if (Array.isArray(values.logo)) return true;
  return false;
}

const EditTeam = (props: EditTeamProps) => {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const toastApiError = useToastApiError();

  const { team, tournamentId } = props;

  const handleCancelClick = useCallback(() => {
    navigate(Pages.TEAMS.INDEX, { replace: true });
  }, [navigate]);

  const handleSubmit = useCallback<SubmitTeamForm>(
    ({ values }) => {
      if (!isChanged(team, values)) {
        handleCancelClick();
        toast.info(t`Ничего не изменилось`);
        return Promise.resolve(true);
      }

      return (
        dispatch(
          editTeam({
            tournamentId,
            id: team.id,
            name: values.name,
            shortName: values?.shortName ?? '',
            logo: mapLogo(values.logo as File[]),
          }),
        )
          .unwrap()
          .then(({ logoError }) => {
            handleCancelClick();
            // Название сохранено, логотип — нет: говорим об этом, а не «успешно».
            if (logoError) toastApiError(logoError, msg`Команда сохранена, но логотип не загрузился`);
            else toast.success(t`Команда успешно обновлена`);
            return true;
          })
          // Не сохранилось — остаёмся на форме с правками, кнопка снова доступна.
          .catch((error) => {
            toastApiError(error, msg`Не удалось сохранить команду`);
            return false;
          })
      );
    },
    [dispatch, handleCancelClick, team, tournamentId, t, toastApiError],
  );

  return <TeamForm initialValues={team} onSubmit={handleSubmit} />;
};

export default EditTeam;
